<template>
  <div>
    <PageHeader />
    <ProjInfo :projectInfo="projectInfo" :changelog="changelog" />

        <v-container class="section-block">

            <v-row>
            <v-col class="col-12" no-gutters>
                <v-responsive :aspect-ratio="16/9">
                    <video width="100%" :src="video.tutorial" :poster="images.poster" controls></video>
                </v-responsive>
            </v-col>
        </v-row>

            <v-row>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <div class=" group-header">Usage</div>
                </v-col>
                <v-col class="col-12 col-md-8"> </v-col>
            <!-- </v-row>
            <v-row> -->
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.newLayer" width="100%" alt="">
                    <h2>Create a Glowkit component</h2>
                    <p>
                        Click to import a very intelligent precomp containing the bars, glows and math to control it all. Everything inside of the precomp is controlled by the <a href="https://www.schoolofmotion.com/blog/master-properties-after-effects#:~:text=Master%20Properties%20is%20a%20feature,not%20affect%20the%20nested%20composition" target="_blank">Essential Properties</a> of the layer. 
                    </p>
                    <p>
                        Access <b>Essential Properties</b> by twirling down the layer just the transform properties.
                    </p>
                    <p>
                        This layer is self-contained and there is no risk of confusing the system. Feel free to trim, split, duplicate as needed.
                    </p>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <!-- <video width="100%" src="/video/Dotkit-Custom.mp4" autoplay loop muted></video> -->
                    <img :src="images.addState" width="100%" alt="">
                    <h2>Add states</h2>
                    <p>
                        There are <a href="#states">6 discrete states</a> and clicking one of these buttons will add a named marker to the Glowkit layer at the playhead.
                    </p>
                    <p>
                        These markers tell the Glowkit extension about timing and where to dynamically create keyframes. Adding a new state will create a marker and update the keyframes.
                    </p>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <video width="100%" :src="video.updateKeyframes" autoplay loop muted />
                    <h2>Update keyframes</h2>
                    <p>
                        Glowkit creates keyframes rather than trying to manage motion with expressions. This enables a major preview and render speed boost. After retiming the state markers, click <b>Update Keyframes</b> to rebuild all the keyframes on the Glowkit layer.
                    </p>
                    <p>
                        All keyframes controlling the animation of the glow states (not layer transforms) will be deleted and new state and <a href="#automatic transitions">transition</a> keys will be added. 
                    </p>
                </v-col>
            </v-row>

            <v-row>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <div class=" group-header">Shape</div>
                </v-col>
                <v-col class="col-12 col-md-8"> </v-col>
                <v-col class="col-12 col-md-6">
                    <p>
                        The basic shape of the glow bar is a rounded rectangle, with a trim path. This is used to create a variety of shapes that conform to different screens. 
                    </p>
                </v-col>
                <v-col class="col-12 col-md-6"> </v-col>

                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.presets" width="100%" alt="">
                    <h2>Shape presets</h2>
                    <p>
                        Within the Glowkit extension panel are 3 presets:                        
                    </p>
                    <ul>
                        <li>Line</li>
                        <li>Circle</li>
                        <li>Rounded rectangle</li>
                    </ul>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.shapeCustom" width="100%" alt="">
                    <h2>Shape customization</h2>
                    <p>
                        These shape presets might not match the device exactly, and additional editing could be necessary. Adjust the properties within the <b>Shape</b> group to customize the path of the glow bar.                        
                    </p>
                    <ul>
                        <li>Size</li>
                        <li>Position</li>
                        <li>Rounding</li>
                        <li>Max shape trim (the absolutely farthest the bar will extend)</li>
                        <li>Trim offset</li>
                    </ul>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.additionalProps" width="100%" alt="">
                    <h2>Additional properties</h2>
                    <p>
                        Beyond the shape, the feel of the glow bar may be adjusted using:                     
                    </p>
                    <ul>
                        <li>Glow enable <i>(toggle off to speed up renders)</i></li>
                        <li>Stroke width <i>(thickness of the bar)</i></li>
                        <li>Glow size <i>(adjust the glow in relation to the bar thickness)</i></li>
                        <li>Light sweep size <i>(how visible the increased glow is during the <b>Thinking</b> state)</i></li>
                        <li>Custom colors</li>
                        <li>Mic placement <i>(position and scale offset)</i></li>
                    </ul>
                </v-col>
            </v-row>


            <v-row>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <div class=" group-header">Edit states</div>
                </v-col>
                <v-col class="col-12 col-md-8"> </v-col>
                <v-col class="col-12 col-md-6">
                    <p>
                        A state is just a layer marker. It may be dragged forward and backward in time. But as you start adding more states it may become difficult to retime all of them. 
                    </p>
                    <p>
                        These buttons are here for support. 
                    </p>
                </v-col>
                <v-col class="col-12 col-md-6"> </v-col>

                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.newLayer" width="100%" alt="">
                    <h2>Shift states</h2>
                    <p>
                        Clicking these arrows will move all markers after the nearest marker (see Remove Nearest) forward/backward in time the number of frames you specify, then <b>Update Keyframes</b>.
                    </p>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.removeNearest" width="100%" alt="">
                    <h2>Remove nearest</h2>
                    <p>
                        After Effects has a weird system of identifying keyframes and markers by their relation to the playhead. Move the playhead near one of the markers and click to remove it from the layer and update the keys.
                    </p>
                    <p>
                        This is an automation of <b>Right click > Delete this Marker</b>, then clicking <b>Update Keyframes</b>.
                    </p>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.newLayer" width="100%" alt="">
                    <h2>Clear all</h2>
                    <p>
                        Sometimes a Glowkit layer becomes too crowded. You can always delete it and create a new one. But if you have the layer positioned just right in the comp and need a fresh start.
                    </p>
                    <p>
                        This is an automation of <b>Right click > Delete all Marker</b>, then clicking <b>Update Keyframes</b>.
                    </p>
                </v-col>
            </v-row>
            
            
            <v-row>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <div class=" group-header">Tips</div>
                </v-col>
                <v-col class="col-12 col-md-8"> </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.stateSettings" width="100%" alt="">
                    <h2>State settings</h2>
                    <p>
                        Great care was given to designing states that will scale to different screens. Because of the size difference between something as small as a watch or as large as a TV, the movement of the states might need to me adjusted per project. 
                    </p>
                    <p>
                        Adjustments may be made to the range of movement or speed for the states:
                    </p>
                    <ul>
                        <li>User input</li>
                        <li>Thinking</li>
                        <li>Heaving</li>
                    </ul>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.bitDepth" width="100%" alt="">
                    <h2>Bit depth</h2>
                    <p>
                        Be sure to set the project to <b>16 bit</b>. This is a setting that is easily missed, but is important to be sure the glow looks right.   
                    </p>
                    <p>
                        ALT/OPT click the bit rate in the project panel to cycle through 8/16/32 bit. It might be tempting set this to 32 bit, but stick with 16 bit to keep the glow on brand. 
                    </p>
                </v-col>

            </v-row>

            <!-- <v-row>
                <v-col class="col-12 col-md-7">
                    <v-img :src="images.transitions"></v-img>
                </v-col>
                <v-col class="col-12 col-md-5">
                    <h2 style="margin-top: 0">Automatic transitons</h2>
                    <p>
                        One of the areas that v1 failed to handle properly was to transitioning between states. It was automatic, but it was little more than a crossfade, and but rarely accurate.
                    </p>
                    <p>
                        The new transition system will now select the appropriate keyframe set to move between states. Kind of like a blend system in a game engine.
                    </p>
                    <br />
                    <br />
                    <h3>Some transtions don't work</h3>
                    <p>
                        The Mic state is unique in that it <a href="#no transition">does not have a transition</a> between Super G and Molecule states. 
                    </p>
                </v-col>
            </v-row> -->
        </v-container>

        
        <v-container class="section-block">
        <v-row>
            <v-col class="col-12 col-sm-6 col-md-4">
                <div class=" group-header">Included states</div>
            </v-col>
        </v-row>
            
        <div v-for="(type, typeIndex) in dotStates" :key="typeIndex" class="state-box">
            <h2 :id="typeIndex">{{ typeIndex | uppercase }}</h2>
            <v-row>
                <v-col v-for="(state, stateIndex) in type" :key="stateIndex" class="col-6 col-sm-4 col-md-3" no-gutters>
                    <img :src="require(`../assets/gif/glowkit/${state}.gif`)" style="width: inherit" :alt="state">
                    <h4>{{ state | removeUnderscores }}</h4>
                </v-col>
            </v-row>
        </div>
    </v-container>

    
    <!-- <InstallInfo :projectInfo="projectInfo" /> -->
    <InstallInfo id="install" name="Glowkit" extension :hosts="['After Effects']"/>

    <Changelog :changelog="changelog" /> 

  </div>
</template>

<script>


export default {
    name: 'dotkit',
    components: {
        // PageHeader,
        // ProjInfo,
        // InstallInfo,
    },
    data: () => ({
        images: {
            poster: require('../assets/images/Glowkit-poster.jpg'),
            transitions: require('../assets/images/Dotkit_transtions.png'),
            newLayer: require('../assets/images/Glowkit-New.jpg'),
            addState: require('../assets/images/Glowkit-AddState.jpg'),
            bitDepth: require('../assets/gif/glowkit/Bitdepth.gif'),
            presets: require('../assets/gif/glowkit/Presets.gif'),
            stateSettings: require('../assets/images/Glowkit-StateSettings.jpg'),
            additionalProps: require('../assets/images/Glowkit-AdditionalProps.jpg'),
            shapeCustom: require('../assets/images/Glowkit-ShapeCustom.jpg'),
            removeNearest: require('../assets/images/Dotkit-RemoveNearest.jpg'),
        },
        video: {
            tutorial: require('../assets/video/Glowkit-tutorial.mp4'),
            updateKeyframes: require('../assets/video/Glowkit-UpdateKeys.mp4'),
            shiftStates: require('../assets/video/Dotkit-ShiftStates.mp4'),
            clearAll: require('../assets/video/Dotkit-ClearAll.mp4'),
        },
        projectInfo: {
            name: 'Glowkit',
            subtitle: 'Animating the glow with states, not keyframes',
            // source: true,
            about: `Based on the powerful system <a href="dotkit">DotKit</a>, Glowkit is designed to empower and simplify experimentation with the glow bar and its states. 
            
            States are managed and transitioned automatically based on a system of markers. Elements loop and randomize to support the organic nature of the glow.`
        },
        changelog: [
            {
                version: '1.0.0',
                date: 'Dec 08, 2020',
                comment: [
                    'Initial release',
                ],
            },
        ],
        dotStates: {
            states: [
                'Closed',
                'Closed_with_mic',
                'Listening',
                'Thinking',
                'User_input',
                'Heaving',
            ],
        }
    }),
    filters: {
        uppercase(str) {
            return str.toUpperCase()
        },
        removeUnderscores(str) {
            return str.replace(/_/g, ' ')
        },
    }
}
</script>

<style scoped>
/* .state-box {
    background: #fff;
    margin: 16px 12px;
    padding: 16px 0;
}
.state-box h3 {
    padding-left: 16px;
} */
.state-box img {
    border: 1px solid rgba(0,0,0,0.03);
}
.state-box .col {
    margin-bottom: 18px;
}
.state-box h2 {
    /* margin-bottom: -54px; */
}

</style>